import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { PaymentSource } from "features/payment-methods/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { patch } from "common/helpers/HTTP";

async function setPaymentSourceRequest(
  workspaceId: string,
  subscriptionId: string,
  paymentSourceId: string,
) {
  await patch(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/payment-source`,
    {
      payment_source_id: paymentSourceId,
    },
  );
}

export default function useSubscriptionPaymentSourceMutations() {
  const queryClient = useQueryClient();
  const selectedWorkspace = useSelectedWorkspaceContext();

  const {
    mutateAsync: setPaymentSource,
    isPending: isSettingPaymentSource,
    error: setPaymentSourceError,
  } = useMutation({
    onMutate: ({ paymentSource, subscriptionId }) => {
      const queryKey =
        CUSTOMER_QUERY_KEYS.paymentSourceKeys.subscription(subscriptionId);

      const originalPaymentSource =
        queryClient.getQueryData<PaymentSource>(queryKey);

      queryClient.setQueryData(queryKey, paymentSource);

      return originalPaymentSource;
    },
    mutationFn: ({
      paymentSource,
      subscriptionId,
    }: {
      paymentSource: PaymentSource;
      subscriptionId: string;
    }) =>
      setPaymentSourceRequest(
        selectedWorkspace.id,
        subscriptionId,
        paymentSource.id,
      ),
    onError: (_, { subscriptionId }, originalPaymentSource) => {
      queryClient.setQueryData(
        CUSTOMER_QUERY_KEYS.paymentSourceKeys.subscription(subscriptionId),
        // if original payment source is undefined, the query update is ignored. this ensures that it updates
        originalPaymentSource || null,
      );
    },
  });

  return {
    setPaymentSource,
    isSettingPaymentSource,
    setPaymentSourceError,
  };
}
